import { HttpClientJsonpModule, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';

import { HttpCacheInterceptorModule, useHttpCacheLocalStorage } from '@ngneat/cashew';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { AccordionModule, TooltipModule } from 'ngx-bootstrap';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ToastContainerDirective, ToastrModule } from 'ngx-toastr';

import { DisplayingConflictPopupComponent } from '@ag-grid-reports/helpers/displaying-conflict-popup/displaying-conflict-popup.component';
import { IframeModeComponent } from '@components/app/iframe-mode';
import { LoginComponent } from '@components/app/login';
import { NoContentComponent } from '@components/app/no-content/no-content';
import { DmComponentsModule } from '@components/dm.components.module';
import { ModalWrapperModule } from '@components/modal-wrapper/modal-wrapper.module';
import { HTTP_CACHE_TTL } from '@config/index';
import { DefaultLayoutComponent } from '@containers/layouts/default/default.component';
import { ExtraLayoutContainerComponent } from '@containers/layouts/extra/extra.container';
import { NotificationsContainerComponent } from '@containers/notifications';
import { environment } from '@environments/environment';
import { ConfigurableSitesEffects } from '@rpt-ngrx/effects/configurable-sites.effects';
import { UiEffects } from '@rpt-ngrx/effects/ui.effects';
import { AgGridI18nService } from '@services/ag-grid-i18n/ag-grid-i18n.service';
import { CustomHttpClient } from '@services/http-client';
import { SERVICE_PROVIDERS } from '@services/index';
import { HTTP_INTERCEPTOR_PROVIDERS } from '@services/interceptors';
import { WINDOW_PROVIDERS } from '@services/window';
import { HELPER_PROVIDERS } from '@shared/helpers';
import { I18nLoader } from '@shared/loaders/i18n-loader';
import { ThemeService } from '@shared/services/theme/theme.service';

import { AppComponent } from './app.component';
import { ROUTES } from './app.routes';
import { CompanyEffects } from './ngrx/effects/company.effects';
import { EventsEffects } from './ngrx/effects/events.effects';
import { ReportsEffects } from './ngrx/effects/reports.effects';
import { SessionEffects } from './ngrx/effects/session.effects';
import { SitesEffects } from './ngrx/effects/sites.effects';
import { metaReducers, reducers } from './ngrx/reducers';
import { XenialUxModule } from './xenial-ux';

const initializeTheme = (themeService: ThemeService) => async () => {
    const result = await themeService.fetchTheme();
    return result;
};

@NgModule({
    bootstrap: [AppComponent],
    declarations: [
        AppComponent,
        NoContentComponent,
        LoginComponent,
        IframeModeComponent,
        NotificationsContainerComponent,

        ExtraLayoutContainerComponent,
        DefaultLayoutComponent,
        LoginComponent,
        DisplayingConflictPopupComponent,
    ],
    imports: [
        BrowserModule,
        TooltipModule.forRoot(),
        DmComponentsModule,
        FormsModule,
        HttpClientModule,
        HttpClientJsonpModule,
        RouterModule.forRoot(ROUTES, { useHash: true }),
        BrowserAnimationsModule,
        AccordionModule.forRoot(),
        ToastrModule.forRoot({ disableTimeOut: 'extendedTimeOut' }),
        ToastContainerDirective,
        XenialUxModule.forRoot(),
        StoreModule.forRoot(reducers, { metaReducers }),
        EffectsModule.forRoot([
            UiEffects,
            SessionEffects,
            ReportsEffects,
            SitesEffects,
            ConfigurableSitesEffects,
            CompanyEffects,
            EventsEffects,
        ]),
        StoreRouterConnectingModule.forRoot(),
        HttpCacheInterceptorModule.forRoot({
            ttl: HTTP_CACHE_TTL,
            responseSerializer(body) {
                return JSON.parse(JSON.stringify({ ...body, isItFromCache: true }));
            },
        }),
        !environment.production ? StoreDevtoolsModule.instrument() : [],
        StoreDevtoolsModule.instrument({
            maxAge: 15,
            logOnly: !!environment.production,
        }),
        TranslateModule.forRoot({
            defaultLanguage: 'default',
            loader: {
                provide: TranslateLoader,
                useClass: I18nLoader,
                deps: [CustomHttpClient, Store, AgGridI18nService],
            },
            extend: true,
        }),
        ModalModule.forRoot(),
        ModalWrapperModule,
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: initializeTheme,
            deps: [ThemeService],
            multi: true,
        },
        useHttpCacheLocalStorage,
        HELPER_PROVIDERS,
        HTTP_INTERCEPTOR_PROVIDERS,
        SERVICE_PROVIDERS,
        WINDOW_PROVIDERS,
    ],
    entryComponents: [DisplayingConflictPopupComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
